
import {
  defineComponent,
  PropType,
  ref,
  reactive,
  computed,
  watch,
  toRef,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Loader from '@/components/layout/Loader.vue';
import { PanelAccordion } from '@/components/flows/operators/shared';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import { TemplatePreview, VoucherConfig, MultiVoucherConfig, MessageConfig, ReuseCouponSelector, UtmConfig } from '@/components/flows/operators/shared';
import SendTestModal from '@/components/modals/SendTestModal.vue';
import SendTestV4Modal from '@/components/modals/SendTestV4Modal.vue';
import { useSendingChannelForm } from '@/composables/flows';
import { useUtmData } from '@/components/flows/operators/shared/utmDefaults';
import { retrieveServicesData } from '@/composables/shop/MyShopParameters';
import { showToastError } from '@/helpers';
import type { SendEmailData } from './index';
import { createDefaultData } from './index';
import SendEmail from './index';

export default defineComponent({
  name: 'ActionSendEmailForm',

  components: {
    Loader,
    PanelAccordion,
    TemplateSelectorV2,
    TemplatePreview,
    VoucherConfig,
    MultiVoucherConfig,
    MessageConfig,
    ReuseCouponSelector,
    UtmConfig,
    SendTestModal,
    SendTestV4Modal,
  },

  props: {
    modelValue: {
      type: Object as PropType<SendEmailData>,
      required: true,
      default: () => createDefaultData(),
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    selectedComponent: {
      type: String,
      default: '',
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const panelsKey = ref(0);

    // Reactive data fields
    const dataFields = reactive<SendEmailData>(createDefaultData({ ...props.modelValue }));

    // Initialize sending channel form composable
    const sendingChannelForm = useSendingChannelForm({
      media: 'email',
      dataFields,
      formValidation: toRef(props, 'formValidation'),
      panelsKey,
      operatorId: props.operatorId,
      onCancel: () => emit('cancel'),
    });

    // Destructure commonly used values from composable
    const {
      campaignId,
      campaignType,
      shopId,
      templateConfig,
      templateSelectorIsOpen,
      formValidationErrors,
      voucherData,
      messageData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      handleSendTest,
      handleSendTestV4,
      handleOpenSendTestModal,
      handleCloseSendTestModal,
      showSendTestModal,
      initialize,
    } = sendingChannelForm;

    // Wrapper to open send test modal with validation
    const openSendTestWithValidation = () => {
      handleOpenSendTestModal(() => SendEmail.validate!(dataFields, { shopId: shopId.value }));
    };

    // UTM data computed
    const utmData = useUtmData(dataFields);

    // Panels configuration (specific to Email)
    const panels = computed(() => [
      {
        id: 'template_preview',
        title: 'automatedScenarios.actions.sending_channel.template_preview.title',
      },
      {
        id: 'voucher_config',
        title: 'automatedScenarios.actions.sending_channel.vouchers_config.title',
        hide: !showVoucherPanel.value,
      },
      {
        id: 'message_config',
        title: 'automatedScenarios.actions.sending_channel.message_config.title',
      },
      {
        id: 'utm_config',
        title: 'automatedScenarios.actions.sending_channel.utm_config.title',
      },
    ]);

    // Emit updates when data changes
    watch(dataFields, () => {
      emit('update:modelValue', createDefaultData({ ...dataFields }));
    }, { deep: true });

    // Initialize on mount
    onMounted(async () => {
      try {
        isLoading.value = true;

        // Load default sender email and name if not set
        const hasExpmail = Object.values(dataFields.template_message_expmail).some((v) => v !== null && v !== '');
        const hasExpname = Object.values(dataFields.template_message_expname).some((v) => v !== null && v !== '');

        if (!hasExpmail || !hasExpname) {
          const field = reactive<Record<string, any>>({
            shopRequiredDataEmail_senderEmail: {},
            shopRequiredDataEmail_senderName: {},
          });
          await retrieveServicesData(shopId.value, field);

          if (!hasExpmail) {
            dataFields.template_message_expmail = field.shopRequiredDataEmail_senderEmail;
          }
          if (!hasExpname) {
            dataFields.template_message_expname = field.shopRequiredDataEmail_senderName;
          }
        }

        // Initialize template config
        await initialize(dataFields.template_display_selector_id);
      } catch (error) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      panelsKey,
      formValidationErrors,
      templateConfig,
      templateSelectorIsOpen,
      dataFields,
      voucherData,
      messageData,
      panels,
      campaignId,
      campaignType,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      handleSendTest,
      handleSendTestV4,
      handleOpenSendTestModal,
      handleCloseSendTestModal,
      showSendTestModal,
      openSendTestWithValidation,
      utmData,
    };
  },
});
